import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import apiClient from "../api/apiClient";
import { useAuth } from "../hooks/useAuth";
import { resolvePostLoginPath } from "../utils/navigationScopes";
import PageLoading from "./PageLoading";
import { clearLoginSession, setLoginSession } from "./LoginProviders";
import { completeOAuthLogin } from "../utils/completeOAuthLogin";
import {
  getGoogleOAuthRedirectUri,
  getMicrosoftOAuthRedirectUri,
} from "../utils/oauthRedirectUris";

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (
      error as {
        response?: { data?: { detail?: { message?: string } | string } };
      }
    ).response?.data?.detail === "object"
  ) {
    const detail = (
      error as { response: { data: { detail: { message?: string } } } }
    ).response.data.detail;
    if (detail?.message) return detail.message;
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: { detail?: string } } }).response
      ?.data?.detail === "string"
  ) {
    return (error as { response: { data: { detail: string } } }).response.data
      .detail;
  }
  return fallback;
}

export default function AuthenticationForm() {
  const { isAuthenticated, mfaClaim, login, scopes, roles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthCallbackStarted = useRef(false);

  const oauthCode = searchParams.get("code");
  const oauthProvider =
    sessionStorage.getItem("oauth_provider") || searchParams.get("provider");
  const hasOAuthCallback = useMemo(
    () => Boolean(oauthCode && oauthProvider),
    [oauthCode, oauthProvider],
  );

  const [isCompletingLogin, setIsCompletingLogin] = useState(
    () => isAuthenticated || hasOAuthCallback,
  );
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fromState = location.state?.from;
  const fromPath =
    typeof fromState === "string"
      ? fromState
      : ((fromState as { pathname?: string } | undefined)?.pathname ?? "/");

  useEffect(() => {
    const oauthError = (location.state as { oauthError?: string } | null)
      ?.oauthError;
    if (oauthError) {
      setErrorMessage(oauthError);
      setIsCompletingLogin(false);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const getOnboardingRequired = async (): Promise<boolean> => {
    const { data } = await apiClient.get<{ onboarding_required: boolean }>(
      "/user/onboarding/status",
    );
    return !!data?.onboarding_required;
  };

  useEffect(() => {
    if (isAuthenticated) {
      setIsCompletingLogin(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const inviteEmail = sessionStorage.getItem("invite_email");
    if (inviteEmail && !email) {
      setEmail(inviteEmail);
    }
  }, [email]);

  useEffect(() => {
    const error = searchParams.get("error");

    if (error) {
      console.error("OAuth error:", error);
      sessionStorage.removeItem("oauth_provider");
      setIsCompletingLogin(false);
      setErrorMessage("Sign-in was cancelled or failed. Please try again.");
      return;
    }

    if (oauthCode && oauthProvider) {
      const dedupeKey = `oauth_code_used_${oauthCode}`;
      if (oauthCallbackStarted.current || sessionStorage.getItem(dedupeKey)) {
        return;
      }
      oauthCallbackStarted.current = true;
      sessionStorage.setItem(dedupeKey, "1");
      setIsCompletingLogin(true);
      setSearchParams({}, { replace: true });
      void handleOAuthCallback(oauthCode, oauthProvider);
      sessionStorage.removeItem("oauth_provider");
    }
  }, [oauthCode, oauthProvider, searchParams, setSearchParams]);

  const handleOAuthCallback = async (code: string, provider: string) => {
    if (provider !== "google") {
      return;
    }

    try {
      await completeOAuthLogin({
        provider: "google",
        code,
        login,
        navigate,
        fromPath,
      });
    } catch (error: unknown) {
      console.error("OAuth callback failed:", error);
      setIsCompletingLogin(false);
      setErrorMessage(
        getApiErrorMessage(error, "Sign-in failed. Please try again."),
      );
    }
  };

  const handleGoogleLogin = async () => {
    try {
      clearLoginSession();
      const response = await apiClient.get(`/auth/google/authorize`, {
        params: { redirect_uri: getGoogleOAuthRedirectUri() },
      });
      sessionStorage.setItem("oauth_provider", "google");
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Failed to get Google auth URL:", error);
      setErrorMessage("Could not start Google sign-in. Please try again.");
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      clearLoginSession();
      const inviteOrgId = sessionStorage.getItem("invite_org_id");
      const response = await apiClient.get(`/auth/microsoft/authorize`, {
        params: {
          redirect_uri: getMicrosoftOAuthRedirectUri(),
          ...(inviteOrgId ? { org_id: Number(inviteOrgId) } : {}),
        },
      });
      sessionStorage.setItem("oauth_provider", "microsoft");
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error("Failed to get Microsoft auth URL:", error);
      setErrorMessage("Could not start Microsoft sign-in. Please try again.");
    }
  };

  const handleEmailNext = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setErrorMessage("Enter your email address to continue.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const { data } = await apiClient.post<{
        context_token: string;
        organization_name: string;
      }>("/auth/login/email", { email: trimmed });
      setLoginSession(data.context_token, data.organization_name);
      navigate("/login/providers", { replace: true });
    } catch (error: unknown) {
      setErrorMessage(
        getApiErrorMessage(
          error,
          "No organization login is configured for this email. Contact your administrator.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    if (mfaClaim === "pending") {
      navigate("/mfa", { replace: true });
      return;
    }

    let cancelled = false;
    const redirectAuthenticatedUser = async () => {
      try {
        if (await getOnboardingRequired()) {
          if (!cancelled) {
            navigate("/onboarding", { replace: true });
          }
          return;
        }
      } catch {
        // If status check fails, preserve existing behavior.
      }

      if (cancelled) return;
      const canAdmin = roles?.some((r) => r === "ADMIN") ?? false;
      navigate(
        resolvePostLoginPath(fromPath, scopes, {
          canAccessAdminRoute: canAdmin,
        }),
        { replace: true },
      );
    };
    void redirectAuthenticatedUser();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, mfaClaim, fromPath, navigate, scopes, roles]);

  if (isCompletingLogin) {
    return <PageLoading />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-emerald-50 via-teal-50 to-cyan-50 px-4 py-8">
      <div className="w-2/3 lg:w-1/2 sm:w-full max-w-lg">
        <div className="relative bg-white rounded-2xl shadow-xl border-2 border-emerald-200 p-6 md:p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="p-3 mb-4">
              <img
                src="/logo.jpeg"
                className="rounded-xl object-cover h-20 w-56"
                alt="Datamint logo"
              />
            </div>
            <h1 className="font-bold bg-linear-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent text-2xl">
              Welcome back
            </h1>
            <p className="text-slate-600 font-medium text-sm mt-1">
              Sign in to continue to your account
            </p>
          </div>

          <div className="w-full space-y-4">
            {errorMessage && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {errorMessage}
              </p>
            )}

            <button
              type="button"
              onClick={() => void handleGoogleLogin()}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-emerald-300 hover:border-emerald-400 hover:bg-emerald-50 text-emerald-700 font-medium py-2.5 px-4 rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              <img src="/google.svg" alt="Google" className="h-5 w-5" />
              Continue with Google
            </button>

            <button
              type="button"
              onClick={() => void handleMicrosoftLogin()}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-teal-300 hover:border-teal-400 hover:bg-teal-50 text-teal-700 font-medium py-2.5 px-4 rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              <img src="/microsoft.svg" alt="Microsoft" className="h-5 w-5" />
              Continue with Microsoft
            </button>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs font-medium uppercase tracking-wide text-slate-500">
                  or use organization email
                </span>
              </div>
            </div>

            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void handleEmailNext();
              }}
            >
              <label
                htmlFor="login-email"
                className="block text-sm font-medium text-slate-700"
              >
                Work email
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorMessage) setErrorMessage(null);
                }}
                placeholder="you@company.com"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? "Checking..." : "Next"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
